import { useContext, useEffect, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { MdArrowForward, MdAssessment, MdPeople, MdSecurity, MdSchool, MdSettings, MdSupervisorAccount } from 'react-icons/md';
import { DashboardContext } from '..';
import SectionButtons from '../../../ReusableComponents/SectionButtons';
import SectionContentHeading from '../../../ReusableComponents/SectionContentHeading';
import RelatoriosGerais from '../../../RelatoriosGerais/RelatoriosGerais';
import ProcessDashboard from './ProcessDashboard';
import './Dashboard.css';

const Cards = () => {
    const dashboard = useContext(DashboardContext);
    const user = useSelector(state => state.AppReducer.user);
    const [activeTab, setActiveTab] = useState(0);
    const [reportType, setReportType] = useState('process');
    const history = useHistory();
    const location = useLocation();
    const processos = dashboard?.processos || [];
    const changeText = !!user?.empresa?.change_text;
    const processLabel = changeText ? 'vaga/cargo' : 'processo seletivo';
    const reportLabel = changeText ? 'Relatórios por vaga/cargo' : 'Relatórios por processo seletivo';
    const institutionPath = `/instituicao/show/${dashboard?.faculdade?.id}`;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const processoId = params.get('processo_id');
        const tab = Number(params.get('tab'));
        const processIndex = processos.findIndex(item => String(item.id) === processoId);
        setActiveTab(processIndex >= 0 ? processIndex : Number.isInteger(tab) && tab >= 0 && tab < processos.length ? tab : 0);
    }, [location.search, dashboard?.processos]);

    const sections = processos.map((item, index) => ({
        value: index, label: item.name, icon: 'relatorios',
        description: `Consulte os indicadores e relatórios ${changeText ? 'desta' : 'deste'} ${processLabel}.`
    }));
    const reportTypes = [
        { value: 'general', label: 'Relatórios gerais', icon: 'relatorios', description: 'Consulte os dados consolidados de candidatos, acessos e resultados.' },
        { value: 'process', label: reportLabel, icon: 'processos', description: `Acompanhe os acessos e a fiscalização de cada ${processLabel}.` }
    ];
    const stats = [
        { title: 'Candidatos', quantity: dashboard?.alunos, icon: MdPeople, tone: 'purple', tab: 4, action: 'Gerenciar candidatos' },
        { title: 'Fiscais', quantity: dashboard?.fiscais, icon: MdSecurity, tone: 'blue', tab: 2, action: 'Gerenciar fiscais' },
        { title: 'Corretores', quantity: dashboard?.corretores, icon: MdSchool, tone: 'orange', tab: 1, action: 'Gerenciar corretores' },
        { title: 'Candidatos online', quantity: dashboard?.alunos_online?.length, icon: MdPeople, tone: 'purple', online: true, tab: 4, action: 'Gerenciar candidatos' },
        { title: 'Fiscais online', quantity: dashboard?.fiscais_online?.length, icon: MdSecurity, tone: 'blue', online: true, tab: 2, action: 'Gerenciar fiscais' },
        { title: 'Avaliadores online', quantity: dashboard?.avaliadores?.length, icon: MdSupervisorAccount, tone: 'green', online: true, tab: 3, action: 'Gerenciar avaliadores' }
    ];
    const selectedTab = activeTab < processos.length ? activeTab : 0;
    const selectedProcess = processos[selectedTab];

    if (dashboard.isLoading) return <div className="dashboard-loading"><CircularProgress /></div>;

    return (
        <div className="module-page module-details institution-dashboard">
            <section className="card module-card dashboard-overview">
                <div className="card-body">
                    <div className="dashboard-heading">
                        <div className="dashboard-identity">
                            <span className="dashboard-heading-icon" aria-hidden="true"><MdAssessment /></span>
                            <div>
                                <p className="dashboard-eyebrow">Dashboard</p>
                                <h1>{dashboard?.faculdade?.name}</h1>
                                <p className="dashboard-intro">Acompanhe os candidatos, a equipe online e os relatórios {changeText ? 'do processo.' : 'da instituição.'}</p>
                            </div>
                        </div>
                        <Link className="dashboard-settings" to={`${institutionPath}?tab=${user?.empresa?.plan?.training ? 6 : 5}`}>
                            <MdSettings aria-hidden="true" /> Configurações
                        </Link>
                    </div>

                    <div className="dashboard-stats">
                        {stats.map(({ title, quantity, icon: Icon, tone, online, tab, action }) => (
                            <Link key={title} className={`dashboard-stat is-${tone}`} to={`${institutionPath}?tab=${tab}`} aria-label={`${title}: ${quantity ?? 0}. ${action}`}>
                                <span className="dashboard-stat-icon" aria-hidden="true"><Icon /></span>
                                <div className="dashboard-stat-content">
                                    <span className="dashboard-stat-label">{online && <span className="dashboard-online-dot" aria-hidden="true" />}{title}</span>
                                    <strong>{Number(quantity ?? 0).toLocaleString('pt-BR')}</strong>
                                    <span className="dashboard-stat-action">{action} <MdArrowForward aria-hidden="true" /></span>
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div className="dashboard-report-picker hidethis">
                        <h2>Escolha o tipo de relatório</h2>
                        <SectionButtons compact variant="modules" label="Tipos de relatório" value={reportType} items={reportTypes} onChange={setReportType} />
                    </div>
                </div>
            </section>

            {reportType === 'general' && <div className="dashboard-general-reports">
                <RelatoriosGerais faculdade_id={dashboard?.faculdade?.id} faculdade_name={dashboard?.faculdade?.name} slug={dashboard?.faculdade?.slug} />
            </div>}

            {reportType === 'process' && <>
                <section className="card module-card detail-summary dashboard-process-picker hidethis">
                    <div className="card-body">
                        <h2 className="module-details-title">{reportLabel}</h2>
                        <p className="dashboard-card-description">Selecione {changeText ? 'a vaga/cargo' : 'o processo seletivo'} que deseja acompanhar.</p>
                        {sections.length > 0 ? <SectionButtons compact variant="modules" label={reportLabel} value={selectedTab} items={sections} onChange={value => {
                            setActiveTab(value);
                            const params = new URLSearchParams(location.search);
                            params.delete('processo_id');
                            params.set('tab', value);
                            history.push({ search: `?${params.toString()}` });
                        }} /> : <p className="dashboard-empty">{changeText ? 'Nenhuma' : 'Nenhum'} {processLabel} disponível para consulta.</p>}
                    </div>
                </section>

                {selectedProcess && <section className="card module-card module-content dashboard-process-content">
                    <div className="card-body">
                        <SectionContentHeading items={sections} value={selectedTab} />
                        <ProcessDashboard key={selectedProcess.id} processo_id={selectedProcess.id} faculdade_id={dashboard?.faculdade?.id} />
                    </div>
                </section>}
            </>}
        </div>
    );
};

export default Cards;
